import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { SelectButton } from "primereact/selectbutton";
import SignatureCanvas from "react-signature-canvas";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/firebase";
import { toast } from "react-toastify";
import { fetchCashRegisters, formatCurrency, submitCashOut } from "../../services/cashService";
import { CURRENCY_OPTIONS } from "../../services/exchangeRateService";
import useViewOnly from "../../hooks/useViewOnly";
import styles from "../shifts/shifts.module.scss";

const CashOut = () => {
    const dispatch = useDispatch();
    // Cash Out itself (controllers/cash.js exports.cashOut, hit via
    // /cash/admin/cash-out) has no shift/workLocation requirement server-side
    // - just admin/handler role - so the only real gate here is the same
    // clocked-in-or-view-only rule every other staff action follows.
    const cashOutBlocked = useViewOnly();
    const [registers, setRegisters] = useState([]);
    const [cashRegisterId, setCashRegisterId] = useState(null);
    const [currency, setCurrency] = useState("usd");
    const [amount, setAmount] = useState(null);
    const [note, setNote] = useState("");
    const [signatureDataUrl, setSignatureDataUrl] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const signatureRef = useRef(null);

    const loadRegisters = async () => {
        const res = await fetchCashRegisters();
        setRegisters(res?.data || []);
    };

    useEffect(() => {
        loadRegisters();
    }, []);

    const registerOptions = registers.map((r) => ({ label: r.name, value: r._id }));

    const clearSignature = () => {
        if (signatureRef.current) signatureRef.current.clear();
        setSignatureDataUrl(null);
    };

    const saveSignature = () => {
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
            setSignatureDataUrl(signatureRef.current.toDataURL());
        }
    };

    const uploadSignature = async () => {
        const blob = await fetch(signatureDataUrl).then((r) => r.blob());
        const storageRef = ref(storage, `cashOuts/signature_${Date.now()}.png`);
        await uploadBytes(storageRef, blob);
        return getDownloadURL(storageRef);
    };

    const startOver = () => {
        setCashRegisterId(null);
        setCurrency("usd");
        setAmount(null);
        setNote("");
        setSignatureDataUrl(null);
        setResult(null);
        loadRegisters();
    };

    const handleSubmit = async () => {
        if (!cashRegisterId) {
            toast.error("Select a register");
            return;
        }
        if (!amount || amount <= 0) {
            toast.error("Enter a valid amount");
            return;
        }
        if (!signatureDataUrl) {
            toast.error("Sign to confirm this cash out");
            return;
        }

        setSubmitting(true);
        try {
            const signatureUrl = await uploadSignature();
            const res = await submitCashOut(dispatch, { cashRegisterId, amount, currency, note, signatureUrl });
            if (res?.success) {
                toast.success("Cash out recorded");
                setResult(res.data);
            }
        } catch (error) {
            console.error("Error recording cash out:", error);
            toast.error("Failed to record cash out");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div className={styles.headerTitleRow}>
                    <div className={styles.headerIcon}>
                        <i className="pi pi-money-bill" />
                    </div>
                    <div>
                        <h2>Cash Out</h2>
                        <p className={styles.subtitle}>Remove cash from a register - signed for accountability</p>
                    </div>
                </div>
            </div>

            {cashOutBlocked ? (
                <div className={styles.sectionCard}>
                    <p className={styles.shiftSub}>You need to be clocked in to record a cash out.</p>
                </div>
            ) : result ? (
                <div className={styles.sectionCard}>
                    <div className={styles.completeSummary}>
                        <div className={styles.completeIcon}>
                            <i className="pi pi-check" />
                        </div>
                        <h4>Cash Out Recorded</h4>
                        <p>{formatCurrency(result.amount, result.currency)} removed. This entry now shows in the register's ledger.</p>
                    </div>
                    <div className={styles.actionRow}>
                        <Button label="Record Another" icon="pi pi-plus" className={styles.primaryButton} onClick={startOver} />
                    </div>
                </div>
            ) : (
                <div className={styles.sectionCard}>
                    <div className={styles.metaGrid}>
                        <div className={styles.metaBox}>
                            <div className={styles.metaLabel}>Register</div>
                            <Dropdown value={cashRegisterId} options={registerOptions} onChange={(e) => setCashRegisterId(e.value)} placeholder="Select a register" style={{ width: "100%" }} />
                        </div>
                        <div className={styles.metaBox}>
                            <div className={styles.metaLabel}>Currency</div>
                            <SelectButton value={currency} options={CURRENCY_OPTIONS} onChange={(e) => e.value && setCurrency(e.value)} />
                        </div>
                        <div className={styles.metaBox}>
                            <div className={styles.metaLabel}>Amount</div>
                            <InputNumber
                                value={amount}
                                onValueChange={(e) => setAmount(e.value)}
                                mode="currency"
                                currency={currency === "jmd" ? "JMD" : "USD"}
                                locale="en-US"
                                min={0}
                                placeholder="0.00"
                                style={{ width: "100%" }}
                            />
                        </div>
                    </div>

                    <div style={{ marginTop: "18px" }}>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Note (optional)</label>
                        <InputTextarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} style={{ width: "100%" }} placeholder="Reason for this cash out" />
                    </div>

                    <div style={{ marginTop: "18px" }}>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Sign to Confirm</label>
                        {signatureDataUrl ? (
                            <div>
                                <img src={signatureDataUrl} alt="Signature preview" className={styles.signaturePreviewLarge} />
                                <Button label="Redo Signature" icon="pi pi-refresh" outlined onClick={() => setSignatureDataUrl(null)} style={{ marginTop: "10px" }} />
                            </div>
                        ) : (
                            <div>
                                <SignatureCanvas ref={signatureRef} canvasProps={{ className: styles.signatureCanvas }} />
                                <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
                                    <Button label="Clear" icon="pi pi-times" outlined severity="danger" onClick={clearSignature} />
                                    <Button label="Save Signature" icon="pi pi-check" onClick={saveSignature} />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={styles.navRow} style={{ marginTop: "20px" }}>
                        <span />
                        <Button label={submitting ? "Recording..." : "Complete Cash Out"} icon={submitting ? "pi pi-spin pi-spinner" : "pi pi-check"} className={styles.dangerButton} onClick={handleSubmit} disabled={submitting} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CashOut;
